import { PptContent } from '../PptContent';

import { DEFAULT_LAYOUT_STYLES } from '../../../constants/pptThemeLayouts';
import { LayoutStyles, SlideContent } from '../../../types/ppt-slide';
import { ContentItem, ContentObject } from '../../../types/ppt-slide';

import { BaseLayoutProps } from './BaseLayout';

interface GridLayoutProps
  extends Omit<BaseLayoutProps, 'formatContentForPptContent'> {
  gridType: LayoutStyles.QUADRANT | LayoutStyles.COMPARISON | LayoutStyles.LEFT_CONTENT | LayoutStyles.RIGHT_CONTENT | LayoutStyles.HORIZONTAL_SPLIT | LayoutStyles.VERTICAL_SPLIT;
}

interface QuadrantStyles {
  container: string;
  gridItem: string;
  typography: {
    content: { className: string };
  };
}

interface ColumnStyles {
  container: string;
  columnHeader: string;
  typography: {
    content: { className: string };
  };
  columnContainer?: string;
  divider?: string;
}

interface ContentSideStyles {
  container: string;
  wrapper: string;
  typography: {
    content: { className: string };
  };
}

const getQuadrantStyles = () => {
  const defaultStyles = DEFAULT_LAYOUT_STYLES[LayoutStyles.QUADRANT];

  return {
    container: defaultStyles.container,
    gridItem: defaultStyles.gridItem,
    typography: defaultStyles.typography,
  };
};

const getColumnStyles = () => {
  const defaultStyles = DEFAULT_LAYOUT_STYLES[LayoutStyles.COMPARISON];

  return {
    container: defaultStyles.container,
    columnHeader: defaultStyles.columnHeader,
    typography: defaultStyles.typography,
  };
};

const getLeftContentStyles = () => {
  const defaultStyles = DEFAULT_LAYOUT_STYLES[LayoutStyles.LEFT_CONTENT];

  return {
    container: defaultStyles.container,
    wrapper: defaultStyles.wrapper,
    typography: defaultStyles.typography,
  };
};

const getRightContentStyles = () => {
  const defaultStyles = DEFAULT_LAYOUT_STYLES[LayoutStyles.RIGHT_CONTENT];

  return {
    container: defaultStyles.container,
    wrapper: defaultStyles.wrapper,
    typography: defaultStyles.typography,
  };
};

const getHorizontalSplitStyles = () => {
  const defaultStyles = DEFAULT_LAYOUT_STYLES[LayoutStyles.HORIZONTAL_SPLIT];

  return {
    container: defaultStyles.container,
    columnHeader: defaultStyles.columnHeader,
    typography: defaultStyles.typography,
  };
};

const getVerticalSplitStyles = () => {
  const defaultStyles = DEFAULT_LAYOUT_STYLES[LayoutStyles.VERTICAL_SPLIT];

  return {
    container: defaultStyles.container,
    columnHeader: defaultStyles.columnHeader,
    typography: defaultStyles.typography,
  };
};

const QuadrantItem = ({
  item,
  fontFamily,
  textColor,
  styles,
}: {
  item: ContentItem;
  fontFamily: string;
  textColor: string;
  styles: QuadrantStyles;
}) => (
  <div
    className={
      styles.gridItem ||
      'flex items-center justify-center rounded p-2 overflow-hidden'
    }
  >
    <PptContent
      className={styles.typography?.content?.className || ''}
      content={item.content || ''}
      fontFamily={fontFamily}
      textColor={textColor}
    />
  </div>
);

const ColumnItem = ({
  column,
  index,
  totalColumns,
  fontFamily,
  textColor,
  styles,
}: {
  column: ContentObject;
  index: number;
  totalColumns: number;
  fontFamily: string;
  textColor: string;
  styles: ColumnStyles;
}) => {
  const columnContainerClass = styles.columnContainer || '';
  const dividerClass = index < totalColumns - 1 ? 'pr-4' : 'pl-4';

  return (
    <div
      className={`flex flex-1 flex-col ${columnContainerClass} ${dividerClass} overflow-hidden`}
    >
      {column.title && (
        <h3
          className={
            styles.columnHeader ||
            'mb-3 text-center text-[min(3cqw,6cqh)] font-semibold'
          }
        >
          {column.title}
        </h3>
      )}
      <div className="flex-1 whitespace-pre-wrap">
        <PptContent
          className={styles.typography?.content?.className || ''}
          content={column.content || ''}
          fontFamily={fontFamily}
          textColor={textColor}
        />
      </div>
    </div>
  );
};

const ContentSideItem = ({
  content,
  fontFamily,
  textColor,
  styles,
  layout,
}: {
  content: SlideContent;
  fontFamily: string;
  textColor: string;
  styles: ContentSideStyles;
  layout: string;
}) => {
  let contentValue = '';
  
  if (typeof content === 'string') {
    contentValue = content;
  } else if (Array.isArray(content)) {
    contentValue = content.length > 0 ? 
      ('content' in content[0] ? content[0].content : '') : '';
  } else if (content && 'content' in content) {
    contentValue = content.content;
  }
  
  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <PptContent
          className={styles.typography?.content?.className || ''}
          content={contentValue}
          fontFamily={fontFamily}
          layout={layout}
          textColor={textColor}
        />
      </div>
    </div>
  );
};

export function GridLayout({
  content,
  fontFamily,
  textColor,
  gridType,
}: GridLayoutProps) {
  if (Array.isArray(content) && (gridType === LayoutStyles.QUADRANT || 
      gridType === LayoutStyles.COMPARISON || 
      gridType === LayoutStyles.HORIZONTAL_SPLIT || 
      gridType === LayoutStyles.VERTICAL_SPLIT)) {
    if (gridType === LayoutStyles.QUADRANT) {
      const styles = getQuadrantStyles();
      return (
        <div
          className={
            styles.container || 'grid h-full grid-cols-2 grid-rows-2 gap-4'
          }
        >
          {content.map((item, index) => (
            <QuadrantItem
              key={index}
              fontFamily={fontFamily}
              item={item as ContentItem}
              styles={styles}
              textColor={textColor}
            />
          ))}
        </div>
      );
    } else if (gridType === LayoutStyles.HORIZONTAL_SPLIT) {
      const styles = getHorizontalSplitStyles();
      return (
        <div
          className={styles.container || 'flex h-full flex-row gap-4'}
        >
          {content.map((column, index) => (
            <ColumnItem
              key={index}
              column={column as ContentObject}
              fontFamily={fontFamily}
              index={index}
              styles={styles}
              textColor={textColor}
              totalColumns={content.length}
            />
          ))}
        </div>
      );
    } else if (gridType === LayoutStyles.VERTICAL_SPLIT) {
      const styles = getVerticalSplitStyles();
      return (
        <div
          className={styles.container || 'flex h-full flex-col gap-4'}
        >
          {content.map((column, index) => (
            <ColumnItem
              key={index}
              column={column as ContentObject}
              fontFamily={fontFamily}
              index={index}
              styles={styles}
              textColor={textColor}
              totalColumns={content.length}
            />
          ))}
        </div>
      );
    } else {
      const styles = getColumnStyles();
      return (
        <div
          className={styles.container || 'flex h-full flex-col gap-4 sm:flex-row'}
        >
          {content.map((column, index) => (
            <ColumnItem
              key={index}
              column={column as ContentObject}
              fontFamily={fontFamily}
              index={index}
              styles={styles}
              textColor={textColor}
              totalColumns={content.length}
            />
          ))}
        </div>
      );
    }
  } else {
    if (gridType === LayoutStyles.LEFT_CONTENT) {
      const styles = getLeftContentStyles();
      return (
        <ContentSideItem
          content={content}
          fontFamily={fontFamily}
          styles={styles}
          textColor={textColor}
          layout="leftContent"
        />
      );
    } else if (gridType === LayoutStyles.RIGHT_CONTENT) {
      const styles = getRightContentStyles();
      return (
        <ContentSideItem
          content={content}
          fontFamily={fontFamily}
          styles={styles}
          textColor={textColor}
          layout="rightContent"
        />
      );
    }
    
    return null;
  }
}
